/* eslint-disable no-param-reassign */
import produce from 'immer';
import { ADDON_ADDRESSES, ADDON_CART_VALIDATION } from '../../util/actionAddons';
import { PRODUCT_GROUPS } from './structures';
import { findProductInfo } from '../../productsInfo';
import { findProductAddress } from '../../addressBook/constants';
import { isEqual } from '../../util/lodash';

// tallies number of occurrences of each value in given array (key = array value, value = num occurrences)
const tallyMap = arr => arr.reduce((acc, v) => acc.set(v, (acc.get(v) || 0) + 1), new Map());

// sorts unique values in array by number of occurrences
const sortByFrequency = arr => [...tallyMap(arr)].sort((a, b) => b[1] - a[1]).map(entry => entry[0]);

/**
 * Returns an array of address ID strings. (addressId or shipToId)
 * @param checkoutOptions
 * @returns {[]}
 */
export const buildSavedAddressIds = checkoutOptions => {
  const savedAddresses = [];

  if (checkoutOptions) {
    const { comp, billable } = checkoutOptions;

    if (comp) {
      if (comp.shipping.addressId) savedAddresses.push(comp.shipping.addressId);
      if (comp.shipping.shipToId) savedAddresses.push(comp.shipping.shipToId);
    }
    if (billable && billable.shipping.shipToId) savedAddresses.push(billable.shipping.shipToId);
  }

  return savedAddresses;
};

export const buildCartProductGroups = (action, cart) =>
  produce(cart, draftCart => {
    const addresses = action.addon[ADDON_ADDRESSES];
    if (!addresses) throw new Error(`The Addresses Addon is required for the event type: ${action.type}`);

    const cartValidation = action.addon[ADDON_CART_VALIDATION];
    if (!cartValidation) throw new Error(`The Cart Validation Addon is required for the event type: ${action.type}`);

    const { productsInfoList } = cartValidation;

    // Clear every cartProductList and Label in all groups before we begin
    draftCart.productGroups.forEach(group => {
      group.cartProductIds.length = 0;
    });

    const {
      products,
      headers: { legacyCheckoutOptions }
    } = draftCart;
    const savedAddressIds = buildSavedAddressIds(legacyCheckoutOptions);
    const cartProducts = products.map(cartProduct => {
      const productInfo = findProductInfo(productsInfoList, cartProduct.productCode);
      const isDirectShip = !!productInfo?.directShip;
      return { ...cartProduct, isDirectShip };
    });

    Object.entries(PRODUCT_GROUPS).forEach(([type, entry]) => {
      const group = draftCart.productGroups.find(group => group.type === type);
      const groupItems = cartProducts.filter(cartProduct => entry.groupBy(cartProduct));
      const availableAddresses = addresses.filter(address => entry.addressFilterBy(address, cartProducts, groupItems));

      group.hasHiddenAddresses = type === 'NORMAL' && availableAddresses.every(address => address.isCustom);
      group.labelId = typeof entry.labelId === 'function' ? entry.labelId(cartProducts, groupItems) : entry.labelId;
      group.type = type;
      group.cartProductIds = groupItems.map(item => item.id);
      group.fundamentalCartProductIds = groupItems.filter(item => item.fundamentalRuleId).map(item => item.id);
      group.fundamentalCartProductQuantity = groupItems
        .filter(item => item.fundamentalRuleId)
        .reduce((a, item) => a + item.totals.unitCount, 0);
      group.nonFundamentalCartProductIds = groupItems.filter(item => !item.fundamentalRuleId).map(item => item.id);
      group.nonFundamentalCartProductQuantity = groupItems
        .filter(item => !item.fundamentalRuleId)
        .reduce((a, item) => a + item.totals.unitCount, 0);
      group.availableAddresses = availableAddresses;
      group.canAddAddresses = entry.canAddAddresses;
      group.subtitleId = entry.subtitleId;

      // if no group address is assigned, but at least one member of the group has an addressId...
      if (!group.address && groupItems.some(item => item.addressId)) {
        // find the 'best guess' address ID (the ID contained by the greatest number of group members)
        const addressIdValues = groupItems.map(item => item.addressId);
        const bestGuessAddressId = sortByFrequency(addressIdValues).filter(Boolean)[0];

        if (bestGuessAddressId) {
          const { addressType } = groupItems.find(item => item.addressId === bestGuessAddressId);
          const address = findProductAddress(availableAddresses, bestGuessAddressId, addressType);
          group.address = address;
        }
      }

      // True if there is a selected address and it is still an available address
      const selectedAddressIsAvailable =
        group.address && availableAddresses.some(address => isEqual(group.address, address));

      // If selected address is no longer available, de select it
      if (!selectedAddressIsAvailable) group.address = null;
      // Attempt to pre-select an address if none are selected
      if (!group.address) {
        // Prioritize selecting by custom address
        let savedAddress = availableAddresses.find(address => savedAddressIds.includes(address.id));
        // If no selected address is find, match by ship to id
        if (!savedAddress) {
          savedAddress = availableAddresses.find(address => savedAddressIds.includes(address.originalShipToId));
        }
        // If there is previously saved address, select it.
        if (savedAddress) {
          group.address = savedAddress;
        }
        // If there is only 1 available address, select it.
        else if (availableAddresses.length === 1) {
          // eslint-disable-next-line prefer-destructuring
          group.address = availableAddresses[0];
        }
      }
    });
  });
